"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Sprout, Droplets, TestTube, Shield } from "lucide-react";
export default function ProcesoDelOlivarALaBotella() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: false,
    threshold: 0.1
  });
  const timelineRef = useRef<HTMLDivElement>(null);
  const [fillPercentage, setFillPercentage] = useState(0);
  const stages = [{
    id: 1,
    title: "Cuidado del Olivar",
    description: "Cultivamos nuestros olivos con técnicas sostenibles y respetuosas con el medio ambiente. Cada árbol recibe atención personalizada para garantizar la salud del olivar.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1552070/img/olive_grove_care.png",
    icon: Sprout
  }, {
    id: 2,
    title: "Cosecha Seleccionada",
    description: "Recolectamos las aceitunas en su punto óptimo de maduración, seleccionando manualmente cada fruto para asegurar la máxima calidad del aceite final.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1552070/img/olive_harvest_selection.png",
    icon: Droplets
  }, {
    id: 3,
    title: "Extracción en Frío",
    description: "Procesamos las aceitunas mediante extracción en frío, preservando todas las propiedades organolépticas y nutricionales del aceite virgen extra.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1552070/img/cold_extraction_process.png",
    icon: TestTube
  }, {
    id: 4,
    title: "Control de Calidad",
    description: "Cada lote se elabora con el máximo cuidado, preservando la calidad del aceite desde el olivar hasta la botella. El resultado es un aceite de oliva virgen extra equilibrado, auténtico y lleno de carácter.",
    image: "https://wise-hagfish.10web.cloud/wp-content/uploads/2026/03/openart-image_1773244950053_52b4eefc_1773244950093_d8ae5813.png?v=aix8ndy0qeg",
    icon: Shield
  }];
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;
      const timelineElement = timelineRef.current;
      const rect = timelineElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementHeight = rect.height;
      const startFill = windowHeight * 0.7;
      const endFill = -elementHeight + windowHeight * 0.3;
      if (elementTop <= startFill && elementTop >= endFill) {
        const scrollProgress = (startFill - elementTop) / (startFill - endFill);
        const percentage = Math.min(Math.max(scrollProgress * 100, 0), 100);
        setFillPercentage(percentage);
      } else if (elementTop > startFill) {
        setFillPercentage(0);
      } else if (elementTop < endFill) {
        setFillPercentage(100);
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="proceso-del-olivar-a-la-botella" ref={sectionRef} className="bg-background text-foreground py-48 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-4">
            Del Olivar a la Botella
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Descubre el proceso artesanal que convierte nuestras aceitunas en aceite de oliva virgen extra de máxima calidad.
          </p>
        </div>

        <div ref={timelineRef} className="relative">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary/20 md:-translate-x-1/2">
            <div className="absolute top-0 left-0 w-full bg-primary transition-all duration-300 ease-out" style={{
            height: `${fillPercentage}%`
          }} />
          </div>

          {}
          <div className="space-y-24">
            {stages.map((stage, index) => {
            const {
              ref: stageRef,
              isVisible: stageVisible
            } = useRevealOnScroll({
              once: false,
              threshold: 0.3
            });
            const isEven = index % 2 === 0;
            return <div key={stage.id} data-index={index} ref={stageRef} className={`relative flex flex-col md:flex-row items-center gap-8 ${isEven ? "md:flex-row" : "md:flex-row-reverse"} transition-all duration-700 ${stageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                  {}
                  <div className={`absolute left-8 md:left-1/2 w-4 h-4 rounded-full border-2 border-primary md:-translate-x-1/2 z-10 transition-all duration-500 ${fillPercentage >= (index + 1) / stages.length * 100 ? "bg-primary" : "bg-background"}`}>
                    <div className={`absolute inset-0 rounded-full transition-all duration-500 ${fillPercentage >= (index + 1) / stages.length * 100 ? "bg-primary animate-ping opacity-75" : ""}`} />
                  </div>

                  {}
                  <div className={`w-full md:w-5/12 ${isEven ? "md:pr-12 pl-16 md:pl-0" : "md:pl-12 pl-16 md:pr-0"}`}>
                    <div className="relative aspect-[4/3] rounded-md overflow-hidden">
                      <img src={stage.image} alt={stage.title} className="w-full h-full object-cover" />
                      <div className="absolute top-4 left-4 bg-primary/90 backdrop-blur-sm p-3 rounded-md">
                        <stage.icon className="w-6 h-6 text-accent-foreground" />
                      </div>
                    </div>
                  </div>

                  {}
                  <div className={`w-full md:w-5/12 ${isEven ? "md:pl-12 pl-16 md:pr-0" : "md:pr-12 pl-16 md:pl-0"}`}>
                    <div className="bg-white/50 backdrop-blur-sm rounded-lg p-8">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="font-serif text-primary text-xl font-bold">
                          {String(stage.id).padStart(2, "0")}
                        </span>
                        <h3 className="font-serif text-2xl font-semibold text-foreground">
                          {stage.title}
                        </h3>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {stage.description}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}